import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type { AnalyticsRange, ReviewsSummary, TextAnalyticsView } from '../analytics/types';

/**
 * Text review requests (PRD 5): the practice-facing API under
 * /locations/:locationId/review-sms. Every route 404s without the `reviewsSms` practice flag.
 */

export type SmsProgramStatus =
  | 'not_started'
  | 'setup'
  | 'awaiting_registration'
  | 'live'
  | 'paused';

export interface SendingNumberSummary {
  mode: 'new_local' | 'practice_hosted' | 'toll_free' | null;
  last4: string | null;
  masked: string | null;
  provisioningState: 'none' | 'ordered' | 'porting' | 'loa_pending' | 'active' | 'failed' | string;
  provisioningError: string | null;
}

export interface RegistrationSummary {
  path: '10dlc' | 'toll_free' | null;
  brandState: string;
  campaignState: string;
  approved: boolean;
  rejectionReason: string | null;
  submittedAt: string | null;
  lastCheckedAt: string | null;
}

/** How Get reviews asks patients: by link/QR code or by text after the visit, one at a time. */
export type RequestMethod = 'link' | 'text';

export interface ProgramView {
  /** Get reviews' one active method; texts only go out when it is `text`. */
  requestMethod: RequestMethod;
  status: SmsProgramStatus;
  pausedReason: string | null;
  timing: {
    delayMinutes: number;
    windowStartLocal: string;
    windowEndLocal: string;
    hardLimits: { start: string; end: string };
    sendOnSundays: boolean;
    reminderEnabled: boolean;
    staleCutoffHours: number;
    bettyTuningEnabled: boolean;
    /** The zone every text time uses: the one set in Setup, else the address's. */
    practiceTimezone: string;
    timezone: TextTimezone;
  };
  messages: {
    firstText: string;
    firstTextVersion: number;
    reminderText: string;
    reminderTextVersion: number;
    mentionProvider: boolean;
    privateFeedbackRecipientIds: string[];
  };
  routine: {
    frontDeskPhoneMasked: string | null;
    eodReminderEnabled: boolean;
    eodReminderLocal: string;
    /** Who gets the end-of-day reminder email. */
    eodRecipients: { name: string | null; emailMasked: string }[];
  };
  sendingNumber: SendingNumberSummary;
  registration: RegistrationSummary;
  test: { enabled: boolean; phoneMasked: string | null; usesSharedNumber: boolean };
  liveSince: string | null;
  rosterColumnMap: Record<string, string> | null;
  /** Betty's running two-week test (one at a time): what an edit in Setup would end. */
  tests?: { kind: 'timing' | 'template'; description: string; endsAt: string }[];
}

/** Where the text time zone came from, and any zone that disagrees with it (backend textTimezone.service). */
export interface TextTimezone {
  zone: string;
  source: 'texts_setting' | 'address' | 'practice_setting';
  textsSetting: string | null;
  addressZone: string | null;
  practiceZone: string;
  conflicts: Array<{ kind: 'address' | 'practice_setting'; zone: string }>;
}

/** A number to buy: local (10DLC registration) or toll-free (toll-free verification). */
export type NumberType = 'local' | 'toll_free';

export interface ProgramSettingsInput {
  delayMinutes?: number;
  windowStartLocal?: string;
  windowEndLocal?: string;
  sendOnSundays?: boolean;
  reminderEnabled?: boolean;
  bettyTuningEnabled?: boolean;
  mentionProvider?: boolean;
  privateFeedbackRecipientIds?: string[];
  frontDeskPhone?: string | null;
  eodReminderEnabled?: boolean;
  eodReminderLocal?: string;
  firstText?: string;
  reminderText?: string;
  /** The text time zone (IANA); wins over the practice's own setting. Null: follow the address. */
  textTimezone?: string | null;
}

export interface TemplateValidation {
  ok: boolean;
  errors: string[];
  warnings: string[];
  segments: number;
  gsm7: boolean;
  characters: number;
}

export interface EligibilityResult {
  e164Masked: string;
  eligible: boolean;
  detail: string | null;
  recommendation: 'practice_hosted' | 'new_local';
}

export interface AvailableNumber {
  e164: string;
  locality?: string | null;
  region?: string | null;
}

export interface RegistrationPrefill {
  path: '10dlc' | 'toll_free';
  brand: {
    displayName: string;
    companyName: string;
    ein: string;
    phone: string;
    street: string;
    city: string;
    state: string;
    postalCode: string;
    email: string;
    website: string;
    vertical: string;
    entityType: 'PRIVATE_PROFIT';
  };
  campaign: {
    description: string;
    messageFlow: string;
    sampleMessages: string[];
    helpMessage: string;
    optOutMessage: string;
  };
}

export interface RegistrationSubmission extends RegistrationPrefill {
  contact?: { firstName: string; lastName: string; email: string; phone: string };
}

// ── Today ────────────────────────────────────────────────────────────────────

export type TextStatus =
  | { kind: 'sent' }
  | { kind: 'queued' }
  | { kind: 'waiting_confirmation' }
  | { kind: 'scheduled' }
  | { kind: 'skipped'; reason: string }
  | { kind: 'no_show' | 'cancelled' | 'excluded' };

export type AppointmentStatus = 'scheduled' | 'completed' | 'no_show' | 'cancelled' | 'excluded';

export interface BulkDeleteResult {
  /** Test conversations / visits removed completely. */
  deleted: number;
  /** Patient conversations whose texts were erased (the visit and the stats stay). */
  redacted: number;
  failed: { id: string; message: string }[];
}

export interface TodayRow {
  id: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  patient: string;
  providerName: string | null;
  mobileMasked: string;
  lineType: 'mobile' | 'landline' | 'voip' | 'unknown';
  consent: 'yes' | 'no' | 'unknown';
  status: AppointmentStatus;
  confirmedAt: string | null;
  externalRef: string | null;
  isTest: boolean;
  /** Betty's thread for this visit, once its text exists. */
  conversationId: string | null;
  text: TextStatus;
}

export interface TodayView {
  date: string;
  timezone: string;
  rows: TodayRow[];
  unconfirmed: number;
}

/** A location's Oryx connection (the daily appointments pull). Never carries the login. */
export interface OryxConnectionView {
  connected: boolean;
  status: 'connected' | 'error' | 'disconnected';
  host: string | null;
  timezone: string | null;
  lastSyncAt: string | null;
  lastRun: {
    day: string;
    rostered: number;
    consented: number;
    created: number;
    updated: number;
    completed?: number;
    at: string;
  } | null;
  lastError: { day: string; message: string; at: string } | null;
}

export interface OryxConnectionInput {
  baseUrl: string;
  username: string;
  password: string;
  timezone: string;
}

export type RosterColumnMapping = Record<string, string | null>;

export interface RosterSummary {
  rows: number;
  textable: number;
  created: number;
  updated: number;
  issues: Record<string, number>;
  skipped: Record<string, number>;
}

export type IngestResult =
  | {
      needsMapping: true;
      headers: string[];
      suggestedMapping: RosterColumnMapping;
      missing: Array<{ field: string; label: string }>;
      sampleRows: Record<string, string>[];
    }
  | { needsMapping: false; uploadId: string; summary: RosterSummary };

/** Today → Add a visit (POST roster/visit). */
export interface ManualVisitInput {
  firstName: string;
  lastName?: string;
  mobile: string;
  /** YYYY-MM-DD in the text time zone. */
  date: string;
  /** HH:MM (24h, a time input's value). */
  time: string;
  /** Visit length; the backend defaults to 60. */
  minutes?: number;
  provider?: string;
  consent: 'yes' | 'unknown';
}

export interface TestSendResult {
  program: ProgramView;
  appointment: TodayRow;
  superseded: number;
}

// ── Heads-ups ────────────────────────────────────────────────────────────────

/** Something the practice should fix (the same heads-ups the watchdog emails). */
export interface HeadsUp {
  kind: string;
  title: string;
  body: string;
  link: { label: string; path: string };
}

// ── Conversations ────────────────────────────────────────────────────────────

export type InboxFilter = 'needs_you' | 'posted' | 'in_progress' | 'all';

export interface InboxRow {
  id: string;
  state: string;
  channel: 'sms' | 'email';
  participant: { id: string; kind?: string; displayName?: string };
  shortCode: string | null;
  appointmentId: string | null;
  automationPaused: boolean;
  takenOverBy: string | null;
  nextActionAt: string | null;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  closedReason: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  visit: {
    patient: string;
    providerName: string | null;
    mobileMasked: string;
    startsAt: string;
    isTest: boolean;
    /** A test: who tapped "Send test", and when. */
    testSentBy?: string | null;
    testSentAt?: string | null;
  } | null;
  bucket: Exclude<InboxFilter, 'all'>;
  needsYouReason: string | null;
  /** The office's follow-up on Betty's alerts for this thread; null when she never alerted. */
  followUp: FollowUp | null;
}

export interface FollowUp {
  /** An alert from the last 30 days nobody has marked called back. */
  waiting: boolean;
  /** An alert older than 30 days nobody marked: out of Needs you, still markable. */
  olderOpen?: boolean;
  kind: 'private_feedback' | 'clinical' | 'wrong_number' | 'unclear' | 'complaint';
  calledBackAt: string | null;
  calledBackBy: string | null;
  /** Only on a single thread read. */
  note?: string | null;
}

export interface ThreadMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  author: 'agent' | 'staff' | 'participant' | 'system';
  authorRef: string | null;
  kind: string;
  status: string;
  errorCode: string | null;
  segments: number | null;
  createdAt: string;
  body: string | null;
}

export interface InboxView {
  rows: InboxRow[];
  counts: Record<InboxFilter, number>;
}

export interface ThreadView {
  conversation: InboxRow;
  messages: ThreadMessage[];
}

export type ReplyResult =
  | { sent: true; message: ThreadMessage }
  | { sent: false; status: 'refused' | 'deferred'; reason: string; retryAt?: string };

// ── Performance & summary (types in ../analytics/types) ─────────────────────

export type PerformanceRange = AnalyticsRange;

const base = (locationId: string) => `/locations/${encodeURIComponent(locationId)}/review-sms`;
const unwrap = <T>(p: Promise<{ data: ApiResponse<T> }>): Promise<T> => p.then((r) => r.data.data);

export const reviewSmsApi = {
  // Program
  getProgram: (locationId: string) =>
    unwrap(apiClient.get<ApiResponse<ProgramView>>(`${base(locationId)}/program`)),
  updateProgram: (locationId: string, input: ProgramSettingsInput) =>
    unwrap(apiClient.patch<ApiResponse<ProgramView>>(`${base(locationId)}/program`, input)),
  pauseProgram: (locationId: string, reason?: string) =>
    unwrap(
      apiClient.post<ApiResponse<ProgramView>>(`${base(locationId)}/program/pause`, { reason }),
    ),
  setRequestMethod: (locationId: string, method: RequestMethod) =>
    unwrap(
      apiClient.put<ApiResponse<ProgramView>>(`${base(locationId)}/request-method`, { method }),
    ),
  resumeProgram: (locationId: string) =>
    unwrap(apiClient.post<ApiResponse<ProgramView>>(`${base(locationId)}/program/resume`)),
  goLive: (locationId: string) =>
    unwrap(apiClient.post<ApiResponse<ProgramView>>(`${base(locationId)}/program/go-live`)),
  validateTemplate: (locationId: string, kind: 'first_text' | 'reminder', text: string) =>
    unwrap(
      apiClient.post<ApiResponse<TemplateValidation>>(
        `${base(locationId)}/program/validate-template`,
        { kind, text },
      ),
    ),

  // Test & go live
  /** `replace`: end a test conversation already in progress on this phone (the user chose "Start over"). */
  sendTest: (locationId: string, phone: string, replace = false) =>
    unwrap(
      apiClient.post<ApiResponse<TestSendResult>>(`${base(locationId)}/test/send`, {
        phone,
        ...(replace ? { replace: true } : {}),
      }),
    ),
  disableTestMode: (locationId: string) =>
    unwrap(apiClient.delete<ApiResponse<ProgramView>>(`${base(locationId)}/test`)),

  // Number + registration (read-only; requests go through Settings → Communication → Texting)
  getEligibility: (locationId: string, number?: string) =>
    unwrap(
      apiClient.get<ApiResponse<EligibilityResult>>(
        `${base(locationId)}/provisioning/eligibility`,
        {
          params: number ? { number } : undefined,
        },
      ),
    ),
  searchNumbers: (locationId: string, areaCode?: string, type: NumberType = 'local') =>
    unwrap(
      apiClient.get<ApiResponse<AvailableNumber[]>>(
        `${base(locationId)}/provisioning/available-numbers`,
        { params: type === 'toll_free' ? { type } : areaCode ? { areaCode } : undefined },
      ),
    ),
  refreshProvisioning: (locationId: string) =>
    unwrap(
      apiClient.post<ApiResponse<SendingNumberSummary>>(`${base(locationId)}/provisioning/refresh`),
    ),
  getRegistrationPrefill: (locationId: string) =>
    unwrap(
      apiClient.get<ApiResponse<RegistrationPrefill>>(`${base(locationId)}/registration/prefill`),
    ),
  refreshRegistration: (locationId: string) =>
    unwrap(
      apiClient.post<ApiResponse<{ queued: boolean }>>(`${base(locationId)}/registration/refresh`),
    ),

  // Today
  getDay: (locationId: string, date?: string) =>
    unwrap(
      apiClient.get<ApiResponse<TodayView>>(`${base(locationId)}/appointments`, {
        params: date ? { date } : undefined,
      }),
    ),
  setAppointmentStatus: (
    locationId: string,
    id: string,
    status: AppointmentStatus,
    skipReason?: string,
  ) =>
    unwrap(
      apiClient.patch<ApiResponse<{ appointment: TodayRow; published: string | null }>>(
        `${base(locationId)}/appointments/${encodeURIComponent(id)}`,
        { status, ...(skipReason ? { skipReason } : {}) },
      ),
    ),
  /** Confirms the finished visits of one day (the one on screen), never other days'. */
  confirmFinished: (locationId: string, date: string) =>
    unwrap(
      apiClient.post<ApiResponse<{ confirmed: number }>>(
        `${base(locationId)}/appointments/confirm-finished`,
        { date },
      ),
    ),
  pasteRoster: (
    locationId: string,
    text: string,
    mapping?: RosterColumnMapping,
    saveMapping?: boolean,
  ) =>
    unwrap(
      apiClient.post<ApiResponse<IngestResult>>(`${base(locationId)}/roster/paste`, {
        text,
        ...(mapping ? { mapping } : {}),
        ...(saveMapping !== undefined ? { saveMapping } : {}),
      }),
    ),
  getOryx: (locationId: string) =>
    unwrap(apiClient.get<ApiResponse<OryxConnectionView>>(`${base(locationId)}/oryx`)),
  saveOryx: (locationId: string, input: OryxConnectionInput) =>
    unwrap(apiClient.put<ApiResponse<OryxConnectionView>>(`${base(locationId)}/oryx`, input)),
  disconnectOryx: (locationId: string) =>
    unwrap(apiClient.delete<ApiResponse<OryxConnectionView>>(`${base(locationId)}/oryx`)),
  addVisit: (locationId: string, visit: ManualVisitInput) =>
    unwrap(
      apiClient.post<ApiResponse<Extract<IngestResult, { needsMapping: false }>>>(
        `${base(locationId)}/roster/visit`,
        visit,
      ),
    ),
  uploadRoster: (
    locationId: string,
    file: File,
    mapping?: RosterColumnMapping,
    saveMapping?: boolean,
  ) => {
    const form = new FormData();
    form.append('file', file);
    if (mapping) form.append('mapping', JSON.stringify(mapping));
    if (saveMapping !== undefined) form.append('saveMapping', saveMapping ? 'true' : 'false');
    return unwrap(
      apiClient.post<ApiResponse<IngestResult>>(`${base(locationId)}/roster/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  // Conversations
  listHeadsUps: (locationId: string) =>
    unwrap(apiClient.get<ApiResponse<HeadsUp[]>>(`${base(locationId)}/heads-ups`)),
  listInbox: (locationId: string, filter: InboxFilter) =>
    unwrap(
      apiClient.get<ApiResponse<InboxView>>(`${base(locationId)}/conversations`, {
        params: { filter },
      }),
    ),
  getThread: (locationId: string, id: string) =>
    unwrap(
      apiClient.get<ApiResponse<ThreadView>>(
        `${base(locationId)}/conversations/${encodeURIComponent(id)}`,
      ),
    ),
  takeOver: (locationId: string, id: string) =>
    unwrap(
      apiClient.post<ApiResponse<InboxRow | null>>(
        `${base(locationId)}/conversations/${encodeURIComponent(id)}/take-over`,
      ),
    ),
  /** End the conversation without deleting it (e.g. to start a new test for the same phone). */
  closeConversation: (locationId: string, id: string) =>
    unwrap(
      apiClient.post<ApiResponse<InboxRow | null>>(
        `${base(locationId)}/conversations/${encodeURIComponent(id)}/close`,
      ),
    ),
  handBack: (locationId: string, id: string) =>
    unwrap(
      apiClient.post<ApiResponse<InboxRow | null>>(
        `${base(locationId)}/conversations/${encodeURIComponent(id)}/hand-back`,
      ),
    ),
  /** "Mark called back" (done) or undo the latest marking. Nothing reaches the patient. */
  setFollowUp: (locationId: string, id: string, body: { done: boolean; note?: string | null }) =>
    unwrap(
      apiClient.post<ApiResponse<FollowUp>>(
        `${base(locationId)}/conversations/${encodeURIComponent(id)}/follow-up`,
        body,
      ),
    ),
  deleteConversation: (locationId: string, id: string) =>
    unwrap(
      apiClient.delete<ApiResponse<{ mode: 'deleted' | 'redacted' }>>(
        `${base(locationId)}/conversations/${encodeURIComponent(id)}`,
      ),
    ),
  /** Select → Delete in Conversations: each one as the single Delete does it. */
  bulkDeleteConversations: (locationId: string, ids: string[]) =>
    unwrap(
      apiClient.post<ApiResponse<BulkDeleteResult>>(
        `${base(locationId)}/conversations/bulk-delete`,
        { ids },
      ),
    ),
  /** Select → Delete on Today: test visits only, with their test conversations. */
  bulkDeleteTestVisits: (locationId: string, ids: string[]) =>
    unwrap(
      apiClient.post<ApiResponse<BulkDeleteResult>>(
        `${base(locationId)}/appointments/bulk-delete-tests`,
        { ids },
      ),
    ),
  reply: (locationId: string, id: string, text: string) =>
    unwrap(
      apiClient.post<ApiResponse<ReplyResult>>(
        `${base(locationId)}/conversations/${encodeURIComponent(id)}/reply`,
        { text },
      ),
    ),

  // Performance
  getPerformance: (locationId: string, range: PerformanceRange) =>
    unwrap(
      apiClient.get<ApiResponse<TextAnalyticsView>>(`${base(locationId)}/performance`, {
        params: { range },
      }),
    ),
  /** The Get reviews strip: reviews from both methods. */
  getSummary: (locationId: string, range: PerformanceRange) =>
    unwrap(
      apiClient.get<ApiResponse<ReviewsSummary>>(`${base(locationId)}/summary`, {
        params: { range },
      }),
    ),
};
